import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Touch-friendly input component with:
 * - Minimum 44px height for touch targets (WCAG 2.5.5)
 * - Larger touch-friendly sizing on mobile
 * - Proper touch-action for better scrolling behavior
 */

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, type, ...props }, ref) => {
    // h-11 (44px) to meet touch target requirements, matching icon buttons and default buttons.
    return (
      <input
        type={type}
        className={cn(
          // Base styles with touch-friendly height
          "flex h-11 w-full rounded-md border border-input bg-background px-3 py-2",
          // Typography - base size on mobile for better readability, sm on desktop
          "text-base md:text-sm",
          // Ring offset for focus
          "ring-offset-background",
          // File input styling
          "file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground",
          // Placeholder styling
          "placeholder:text-muted-foreground",
          // Focus states
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
          // Disabled states
          "disabled:cursor-not-allowed disabled:opacity-50",
          // Touch-friendly enhancements - prevent zooming on focus in iOS
          "touch-action-manipulation",
          className
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = "Input"

export { Input }
